import type { LayerSpecification, Map, SourceSpecification } from 'maplibre-gl';
import { useCallback } from 'react';

import type { AcresMapboxSourceIds } from '@/mapbox-layers';
import { priorityLayers } from '@/utils/addLayer';
import Logger from '@/utils/logger';
import { unimplementedFunction } from '@/utils/unimplementedFunction';

export type MapboxHelperUtils = {
  addLayer: (layer: LayerSpecification) => void;
  addLayers: (layers: LayerSpecification[]) => void;
  removeLayer: (layer: LayerSpecification) => void;
  removeLayers: (layers: LayerSpecification[]) => void;
  addSource: (
    sourceId: AcresMapboxSourceIds,
    sourceData: SourceSpecification
  ) => void;
  removeSource: (sourceId: AcresMapboxSourceIds) => void;
  toggleLayerVisibility: (
    layerId: string,
    visibility: 'none' | 'visible'
  ) => void;
};

export const emptyMapboxHelperUtils: MapboxHelperUtils = {
  addLayer: unimplementedFunction,
  addLayers: unimplementedFunction,
  addSource: unimplementedFunction,
  removeLayer: unimplementedFunction,
  removeLayers: unimplementedFunction,
  removeSource: unimplementedFunction,
  toggleLayerVisibility: unimplementedFunction,
};

export const useMapboxHelperUtils = (
  mapInstance: Map | undefined,
  isMapInstanceLoaded: boolean
): MapboxHelperUtils => {
  const sortLayers = useCallback(() => {
    if (!mapInstance || !isMapInstanceLoaded) return;

    priorityLayers.forEach((layerId) => {
      try {
        if (mapInstance.getLayer(layerId)) {
          mapInstance.moveLayer(layerId);
        }
      } catch (error) {
        Logger.error(`Failed to move layer: ${layerId}`);
      }
    });
  }, [isMapInstanceLoaded, mapInstance]);

  const isLayerSourceLoaded = useCallback(
    (layer: LayerSpecification) => {
      if (!mapInstance || !isMapInstanceLoaded) return false;

      if (!('source' in layer) || !layer.source) return false;

      let sourceId: string | undefined;
      if (typeof layer.source === 'string') {
        sourceId = layer.source;
      } else if ('id' in layer) {
        sourceId = layer.id;
      }
      if (!sourceId) return false;

      return mapInstance.isSourceLoaded(sourceId);
    },
    [isMapInstanceLoaded, mapInstance]
  );

  const addLayers = useCallback(
    (layers: LayerSpecification[]) => {
      if (!mapInstance) {
        Logger.error(
          'Attempted to add a layer before the map instance was ready.'
        );
        return;
      }

      if (!isMapInstanceLoaded) {
        Logger.error(
          'Attempted to add a layer before the map instance was fully loaded.'
        );
        return;
      }

      const unreadyLayers = layers.filter(
        (layer) => !isLayerSourceLoaded(layer)
      );
      if (unreadyLayers.length) {
        Logger.error(
          `Attempted to add a layer before the source was ready: ${unreadyLayers
            .map((layer) => layer.id)
            .join(', ')}`
        );
        return;
      }

      layers.forEach((layer) => {
        try {
          mapInstance.addLayer(layer);
        } catch (error) {
          Logger.error(`Failed to add layer: ${layer.id}`);
        }
      });

      sortLayers();
    },
    [isLayerSourceLoaded, isMapInstanceLoaded, mapInstance, sortLayers]
  );

  const addLayer = useCallback(
    (layer: LayerSpecification) => addLayers([layer]),
    [addLayers]
  );

  const removeLayers = useCallback(
    (layers: LayerSpecification[]) => {
      if (!mapInstance) {
        Logger.error(
          'Attempted to remove a layer before the map instance was ready.'
        );
        return;
      }

      if (!isMapInstanceLoaded) {
        Logger.error(
          'Attempted to remove a layer before the map instance was fully loaded.'
        );
        return;
      }

      layers.forEach((layer) => {
        try {
          mapInstance.removeLayer(layer.id);
        } catch (error) {
          Logger.error(`Failed to remove layer: ${layer.id}`);
        }
      });
    },
    [isMapInstanceLoaded, mapInstance]
  );

  const removeLayer = useCallback(
    (layer: LayerSpecification) => removeLayers([layer]),
    [removeLayers]
  );

  const addSource = useCallback(
    (sourceId: AcresMapboxSourceIds, sourceDate: SourceSpecification) => {
      if (!mapInstance) {
        Logger.error(
          'Attempted to add a source before the map instance was initialized.'
        );
        return;
      }

      if (!isMapInstanceLoaded) {
        Logger.error(
          'Attempted to add a source before the map instance was fully loaded.'
        );
        return;
      }

      if (mapInstance.getSource(sourceId)) {
        Logger.error(
          `Attempted to add a source that already exists: ${sourceId}`
        );
        return;
      }

      try {
        mapInstance.addSource(sourceId, sourceDate);
      } catch (error) {
        Logger.error(
          `Failed to add source with id ${sourceId}: ${error.message}`
        );
      }
    },
    [isMapInstanceLoaded, mapInstance]
  );

  const removeSource = useCallback(
    (sourceId: AcresMapboxSourceIds) => {
      if (!mapInstance) {
        Logger.error(
          'Attempted to remove a source before the map instance was initialized.'
        );
        return;
      }

      if (!isMapInstanceLoaded) {
        Logger.error(
          'Attempted to remove a source before the map instance was fully loaded.'
        );
        return;
      }

      try {
        if (!mapInstance.getSource(sourceId)) {
          Logger.error(
            `Attempted to remove a source that does not exist: ${sourceId}`
          );
          return;
        }
      } catch (err) {
        Logger.error(
          `Failed to remove source with id ${sourceId}: ${err.message}`
        );
      }

      const allLayers = mapInstance.getStyle()?.layers;
      try {
        if (allLayers) {
          allLayers.forEach((layer) => {
            if ((layer as any).source === sourceId) {
              mapInstance.removeLayer(layer.id);
            }
          });
        }
      } catch (error) {
        Logger.error(
          `Failed removing layers associated with source ${sourceId}: ${error.message}`
        );
      }

      try {
        mapInstance.removeSource(sourceId);
      } catch (error) {
        Logger.error(
          `Failed to remove source with id ${sourceId}: ${error.message}`
        );
      }
    },
    [isMapInstanceLoaded, mapInstance]
  );

  const toggleLayerVisibility = useCallback(
    (layerId, visibility: 'none' | 'visible') => {
      if (!mapInstance || !isMapInstanceLoaded) return;

      try {
        const layer = mapInstance.getLayer(layerId);
        if (!layer) {
          Logger.error(`Layer not found: ${layerId}`);
          return;
        }

        mapInstance.setLayoutProperty(layerId, 'visibility', visibility);
      } catch (err) {
        Logger.error(`Failed to toggle layer visibility: ${err.message}`);
      }
    },
    [isMapInstanceLoaded, mapInstance]
  );

  return {
    addLayer,
    addLayers,
    addSource,
    removeLayer,
    removeLayers,
    removeSource,
    toggleLayerVisibility,
  };
};
